// @flow strict

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createStructuredSelector } from 'reselect';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';

// Styles
import { MajorKPIBlocksWrapper, StatusWrapper, Separator } from './styles';

// Services
import { selectUser } from 'services/Authentication/selectors';
import { selectAllKPISettings } from 'services/KPISetting/selectors';

// Components
import KPICard from 'components/KPICard';
import { PrimaryButton } from 'components/_ReactUI_V1';

// constants
import { NAVIGATION_ROUTES } from 'utils/constants';
import { KPIGraphColorSchemes } from 'styles/colors';

// Types
import type { HistoryType, IntlType, ImmutableList, LanguageCodeConstant } from 'types';
import type { ImmutableUser } from 'services/Authentication/types';
import type { ImmutableKPICard, ImmutableKPISetting } from 'services/KPISetting/types';
import type { DatasetStatusType } from 'services/Dataset/types';
import { canAccessPlantImportFeatures } from 'utils/authentication';

type Props = {
    kpis: ImmutableList<ImmutableKPICard>,
    plantId: number,
    currentKPISettingId: number,
    updatedAt: string,
    datasetStatus: DatasetStatusType,
    userLanguage: LanguageCodeConstant,
    showMaxCuTransfer: boolean,
    onChangeCurrentKPI: (newKPI: ImmutableKPICard) => void,

    // Injected:
    user: ImmutableUser,
    intl: IntlType,
    history: HistoryType,
};

const TRANSLATION_KEY_PREFIX = 'components.MajorKPITrends'

/*
    Major KPI Trends component
 */
class MajorKPIBlocks extends React.PureComponent<Props> {
    isActiveKPI = (kpi: ImmutableKPICard) =>
        this.props.currentKPISettingId === kpi.get('kpiSettingId');

    getImportedAtText = () =>
        this.props.intl.formatMessage(
            { id: `${TRANSLATION_KEY_PREFIX}.ImportedAt` },
            { date: this.props.updatedAt }
        );

    getDatasetStatusText = () => {
        const datasetStatus = this.props.intl.formatMessage({
            id: `${TRANSLATION_KEY_PREFIX}.DatasetStatuses.${this.props.datasetStatus}`,
        });

        return this.props.intl.formatMessage(
            { id: `${TRANSLATION_KEY_PREFIX}.DatasetStatusLabel` },
            { status: datasetStatus }
        );
    };

    getEditDataButtonText = () =>
        this.props.intl.formatMessage({
            id: `${TRANSLATION_KEY_PREFIX}.EditLatestData`
        });

    getKPISettingForKPICard = (kpi: ImmutableKPICard): ImmutableKPISetting =>
        this.props.kpiSettings.find(
            (kpiSetting: ImmutableKPISetting) => kpiSetting.get('id') === kpi.get('kpiSettingId')
        );

    render() {
        return (
            <MajorKPIBlocksWrapper>
                <StatusWrapper>
                    <span>{this.getImportedAtText()}</span>
                    <span>{this.getDatasetStatusText()}</span>
                    {canAccessPlantImportFeatures(this.props.user) && (
                        <PrimaryButton
                            text={this.getEditDataButtonText()}
                            onClick={() => this.props.history.push(`${NAVIGATION_ROUTES.PLANT}${this.props.plantId}${NAVIGATION_ROUTES.EDIT_LATEST_IMPORT}`)}
                        />
                    )}
                    <Separator />
                </StatusWrapper>
                {this.props.kpis.map((kpi: ImmutableKPICard, index: number) => (
                    <KPICard
                        key={kpi.get('kpiSettingId')}
                        kpi={kpi}
                        kpiSetting={this.getKPISettingForKPICard(kpi)}
                        active={this.isActiveKPI(kpi)}
                        userLanguage={this.props.userLanguage}
                        colorScheme={
                            index < KPIGraphColorSchemes.length
                                ? KPIGraphColorSchemes[index]
                                : KPIGraphColorSchemes[0]
                        }
                        showMaxCuTransfer={this.props.showMaxCuTransfer}
                        onChangeCurrentKPI={this.props.onChangeCurrentKPI}
                        isMajorKPI
                    />
                ))}
            </MajorKPIBlocksWrapper>
        );
    }
}

const mapStateToProps = () =>
    createStructuredSelector({
        user: selectUser(),
        kpiSettings: selectAllKPISettings(),
    });

const mapDispatchToProps = (dispatch: ReduxDispatch) => bindActionCreators({}, dispatch);

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(injectIntl(MajorKPIBlocks))
);
